(() => {
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const currency = document.documentElement.dataset.currency || "USD";
  const money = (cents) => new Intl.NumberFormat("en-US", { style: "currency", currency }).format((Number(cents) || 0) / 100);
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]);

  function setCartOpen(open) {
    const drawer = document.querySelector("[data-cart-drawer]");
    const backdrop = document.querySelector("[data-cart-backdrop]");
    if (!drawer || !backdrop) return;
    drawer.classList.toggle("open", open);
    drawer.setAttribute("aria-hidden", String(!open));
    backdrop.hidden = !open;
    document.body.classList.toggle("cart-open", open);
    document.querySelectorAll("[data-cart-open]").forEach((button) => button.setAttribute("aria-expanded", String(open)));
    if (open) drawer.querySelector("[data-cart-close]")?.focus();
  }

  function cartLine(item, line) {
    const fallbackImages = window.draftedFallbackImages || {};
    const fallbackKey = Object.keys(fallbackImages).find((key) => item.handle && item.handle.startsWith(key));
    const imageSource = item.image || (fallbackKey && fallbackImages[fallbackKey]);
    const imageSeparator = imageSource && imageSource.includes("?") ? "&" : "?";
    const image = imageSource ? `<img src="${escapeHtml(imageSource)}${imageSeparator}width=240" alt="" loading="lazy">` : "";
    const variant = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "Made to order";
    return `<div class="cart-line" data-line="${line}">
      ${image}
      <div class="cart-line-copy">
        <strong>${escapeHtml(item.product_title)}</strong>
        <span>${escapeHtml(variant)}</span>
        <div class="quantity-control" aria-label="Quantity for ${escapeHtml(item.product_title)}">
          <button type="button" data-cart-change="-1" data-line="${line}" data-quantity="${item.quantity}" aria-label="Decrease quantity">−</button>
          <span>${item.quantity}</span>
          <button type="button" data-cart-change="1" data-line="${line}" data-quantity="${item.quantity}" aria-label="Increase quantity">+</button>
        </div>
      </div>
      <b>${money(item.final_line_price)}</b>
    </div>`;
  }

  function renderCart(cart) {
    document.querySelectorAll("[data-cart-count]").forEach((element) => { element.textContent = cart.item_count; });
    const summary = document.querySelector("[data-cart-summary]");
    if (summary) summary.textContent = `${cart.item_count} ${cart.item_count === 1 ? "ITEM" : "ITEMS"}`;
    const lines = document.querySelector("[data-cart-lines]");
    const footer = document.querySelector("[data-cart-footer]");
    if (!lines || !footer) return;
    if (!cart.item_count) {
      lines.innerHTML = `<div class="empty-cart"><strong>YOUR BAG IS READY FOR WORK.</strong><p>Add a DRAFTED statement from the opening collection.</p><button type="button" data-cart-close>Continue shopping</button></div>`;
      footer.hidden = true;
    } else {
      lines.innerHTML = cart.items.map((item, index) => cartLine(item, index + 1)).join("");
      footer.hidden = false;
      const total = footer.querySelector("[data-cart-total]");
      if (total) total.textContent = money(cart.total_price);
    }
  }

  async function getCart() {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Unable to load the bag.");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  function variantsFor(form) {
    const script = form.querySelector("[data-product-variants]");
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch { return []; }
  }

  function syncVariant(form) {
    const selected = [...form.querySelectorAll("[data-product-option]")].map((select) => select.value);
    const variant = variantsFor(form).find((candidate) => candidate.options.every((option, index) => option === selected[index]));
    const id = form.querySelector("[data-variant-id]");
    const submit = form.querySelector('button[type="submit"]');
    if (id && variant) id.value = variant.id;
    if (submit) {
      submit.disabled = !variant || !variant.available;
      submit.textContent = variant && variant.available ? submit.dataset.addLabel || "Add to bag" : "Made to order";
    }
    return variant;
  }

  async function addProduct(form) {
    const submit = form.querySelector('button[type="submit"]');
    const original = submit?.textContent;
    if (submit) { submit.disabled = true; submit.textContent = "Adding…"; }
    const response = await fetch(`${root()}cart/add.js`, { method: "POST", body: new FormData(form), headers: { Accept: "application/json" } });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "This selection could not be added.");
    }
    await getCart();
    setCartOpen(true);
    const live = document.querySelector("[data-cart-live]");
    if (live) live.textContent = "Item added to your bag.";
    if (submit) { submit.disabled = false; submit.textContent = original || "Add to bag"; }
  }

  async function changeLine(line, quantity) {
    const response = await fetch(`${root()}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line, quantity: Math.max(0, quantity) }),
    });
    if (!response.ok) throw new Error("The bag could not be updated.");
    renderCart(await response.json());
  }

  function setFilter(value) {
    const normalized = value.toLowerCase();
    document.querySelectorAll("[data-product-filter]").forEach((button) => {
      const active = button.dataset.productFilter.toLowerCase() === normalized;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", String(active));
    });
    document.querySelectorAll("[data-product-card]").forEach((card) => {
      const type = (card.dataset.productType || "").toLowerCase();
      const collections = (card.dataset.productCollections || "").toLowerCase();
      const tags = (card.dataset.productTags || "").toLowerCase();
      card.hidden = normalized !== "all" && !type.includes(normalized) && !collections.includes(normalized) && !tags.includes(normalized);
    });
  }

  function initProductGallery() {
    const gallery = document.querySelector("[data-product-gallery]");
    const track = gallery?.querySelector("[data-product-gallery-track]");
    if (!gallery || !track) return;

    const items = [...track.querySelectorAll(".shopify-product-media")].filter((item) => item.querySelector("img"));
    if (!items.length) return;

    const previous = gallery.querySelector("[data-product-gallery-previous]");
    const next = gallery.querySelector("[data-product-gallery-next]");
    const count = gallery.querySelector("[data-product-gallery-count]");
    const lightbox = document.querySelector("[data-product-lightbox]");
    const lightboxImage = lightbox?.querySelector("[data-product-lightbox-image]");
    const lightboxViewport = lightbox?.querySelector("[data-product-lightbox-viewport]");
    const lightboxCount = lightbox?.querySelector("[data-product-lightbox-count]");
    const lightboxClose = lightbox?.querySelector("[data-product-lightbox-close]");
    const lightboxPrevious = lightbox?.querySelector("[data-product-lightbox-previous]");
    const lightboxNext = lightbox?.querySelector("[data-product-lightbox-next]");
    const lightboxZoom = lightbox?.querySelector("[data-product-lightbox-zoom]");
    const lightboxBackdrop = lightbox?.querySelector("[data-product-lightbox-backdrop]");
    let activeIndex = 0;
    let scrollFrame = 0;
    let previousFocus = null;
    let pointerDrag = null;
    let suppressCardOpen = false;
    let pointerStart = null;

    const normalizedIndex = (value) => (value + items.length) % items.length;
    const itemImage = (index) => items[index]?.querySelector("img");
    const isLightboxOpen = () => lightbox && !lightbox.hidden;

    function setZoomed(zoomed) {
      if (!lightbox || !lightboxZoom || !lightboxViewport) return;
      lightbox.classList.toggle("is-zoomed", zoomed);
      lightboxZoom.setAttribute("aria-pressed", String(zoomed));
      lightboxZoom.textContent = zoomed ? "Fit whole image" : "Magnify 2×";
      lightboxViewport.scrollTo({ top: 0, left: 0 });
    }

    function updateLightbox() {
      if (!lightboxImage) return;
      const source = itemImage(activeIndex);
      if (!source) return;
      lightboxImage.src = source.currentSrc || source.src;
      lightboxImage.alt = source.alt || "Product image";
      if (lightboxCount) lightboxCount.textContent = `${activeIndex + 1} / ${items.length}`;
    }

    function updateGallery() {
      if (count) count.textContent = `${activeIndex + 1} / ${items.length}`;
      const singleImage = items.length === 1;
      if (previous) previous.hidden = singleImage;
      if (next) next.hidden = singleImage;
      if (lightboxPrevious) lightboxPrevious.hidden = singleImage;
      if (lightboxNext) lightboxNext.hidden = singleImage;
      items.forEach((item, index) => item.setAttribute("aria-current", String(index === activeIndex)));
      if (isLightboxOpen()) updateLightbox();
    }

    function setIndex(value, scroll = true) {
      activeIndex = normalizedIndex(value);
      updateGallery();
      setZoomed(false);
      if (scroll) track.scrollTo({ left: items[activeIndex].offsetLeft, behavior: "smooth" });
    }

    function openLightbox(index) {
      if (!lightbox || !lightboxImage) return;
      previousFocus = document.activeElement;
      activeIndex = normalizedIndex(index);
      lightbox.hidden = false;
      lightbox.setAttribute("aria-hidden", "false");
      document.body.classList.add("product-lightbox-open");
      setZoomed(false);
      updateGallery();
      lightboxClose?.focus();
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightbox.setAttribute("aria-hidden", "true");
      document.body.classList.remove("product-lightbox-open");
      setZoomed(false);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    }

    items.forEach((item, index) => {
      const source = itemImage(index);
      item.tabIndex = 0;
      item.setAttribute("role", "button");
      item.setAttribute("aria-label", `${source?.alt || "Product image"}. Open full-screen viewer.`);
      item.addEventListener("pointerdown", (event) => { pointerStart = { x: event.clientX, y: event.clientY }; });
      item.addEventListener("click", (event) => {
        const moved = pointerStart && Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 12;
        pointerStart = null;
        if (moved) return;
        openLightbox(index);
      });
      item.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openLightbox(index);
        }
      });
    });

    previous?.addEventListener("click", () => setIndex(activeIndex - 1));
    next?.addEventListener("click", () => setIndex(activeIndex + 1));
    lightboxPrevious?.addEventListener("click", () => setIndex(activeIndex - 1));
    lightboxNext?.addEventListener("click", () => setIndex(activeIndex + 1));
    lightboxClose?.addEventListener("click", closeLightbox);
    lightboxZoom?.addEventListener("click", () => setZoomed(!lightbox.classList.contains("is-zoomed")));
    lightboxImage?.addEventListener("click", () => setZoomed(!lightbox.classList.contains("is-zoomed")));
    lightboxBackdrop?.addEventListener("click", (event) => { if (event.target === lightboxBackdrop) closeLightbox(); });

    track.addEventListener("scroll", () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const nearest = items.reduce((best, item, index) => (
          Math.abs(item.offsetLeft - track.scrollLeft) < Math.abs(items[best].offsetLeft - track.scrollLeft) ? index : best
        ), 0);
        if (nearest !== activeIndex) {
          activeIndex = nearest;
          updateGallery();
        }
      });
    }, { passive: true });

    window.addEventListener("resize", () => track.scrollTo({ left: items[activeIndex].offsetLeft }));
    document.addEventListener("keydown", (event) => {
      if (!isLightboxOpen()) return;
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") setIndex(activeIndex - 1);
      if (event.key === "ArrowRight") setIndex(activeIndex + 1);
    });

    updateGallery();
  }

  function initComicStripRail() {
    const section = document.querySelector("[data-comic-strip-section]");
    const rail = section?.querySelector("[data-comic-strip-rail]");
    const cards = rail ? [...rail.querySelectorAll("[data-comic-strip-card]")] : [];
    if (!section || !rail || !cards.length) return;

    const previous = section.querySelector("[data-comic-strip-previous]");
    const next = section.querySelector("[data-comic-strip-next]");
    const count = section.querySelector("[data-comic-strip-count]");
    const lightbox = section.querySelector("[data-comic-strip-lightbox]");
    const lightboxImage = lightbox?.querySelector("[data-comic-strip-lightbox-image]");
    const lightboxTitle = lightbox?.querySelector("[data-comic-strip-lightbox-title]");
    const lightboxCount = lightbox?.querySelector("[data-comic-strip-lightbox-count]");
    const lightboxViewport = lightbox?.querySelector("[data-comic-strip-lightbox-viewport]");
    const lightboxPrevious = lightbox?.querySelector("[data-comic-strip-lightbox-previous]");
    const lightboxNext = lightbox?.querySelector("[data-comic-strip-lightbox-next]");
    const lightboxZoom = lightbox?.querySelector("[data-comic-strip-lightbox-zoom]");
    let activeIndex = 0;
    let scrollFrame = 0;
    let previousFocus = null;

    const normalizedIndex = (value) => (value + cards.length) % cards.length;
    const issueNumber = (value) => String(value + 1).padStart(2, "0");
    const isLightboxOpen = () => lightbox && !lightbox.hidden;
    const cardImage = (index) => cards[index]?.querySelector("img");

    function setZoomed(zoomed) {
      if (!lightbox || !lightboxZoom || !lightboxViewport) return;
      lightbox.classList.toggle("is-zoomed", zoomed);
      lightboxZoom.setAttribute("aria-pressed", String(zoomed));
      lightboxZoom.textContent = zoomed ? "Fit whole image" : "Magnify 2×";
      lightboxViewport.scrollTo({ top: 0, left: 0 });
    }

    function updateLightbox() {
      const source = cardImage(activeIndex);
      if (!source || !lightboxImage) return;
      lightboxImage.src = source.currentSrc || source.src;
      lightboxImage.alt = source.alt || "DRAFTED cartoon strip";
      if (lightboxTitle) lightboxTitle.textContent = cards[activeIndex].dataset.comicTitle || source.alt;
      if (lightboxCount) lightboxCount.textContent = `${issueNumber(activeIndex)} / ${issueNumber(cards.length - 1)}`;
    }

    function updateCounter() {
      if (count) count.textContent = `${issueNumber(activeIndex)} / ${issueNumber(cards.length - 1)}`;
      cards.forEach((card, index) => card.setAttribute("aria-current", String(index === activeIndex)));
      if (isLightboxOpen()) updateLightbox();
    }

    function setIndex(value, scroll = true) {
      activeIndex = normalizedIndex(value);
      setZoomed(false);
      updateCounter();
      if (scroll) rail.scrollTo({ left: cards[activeIndex].offsetLeft, behavior: "smooth" });
    }

    function openLightbox(index) {
      if (!lightbox || !lightboxImage) return;
      previousFocus = document.activeElement;
      activeIndex = normalizedIndex(index);
      lightbox.hidden = false;
      lightbox.setAttribute("aria-hidden", "false");
      document.body.classList.add("comic-strip-lightbox-open");
      setZoomed(false);
      updateCounter();
      lightbox.querySelector("[data-comic-strip-close]:not(.comic-strip-lightbox-backdrop)")?.focus();
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightbox.setAttribute("aria-hidden", "true");
      document.body.classList.remove("comic-strip-lightbox-open");
      setZoomed(false);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    }

    cards.forEach((card, index) => card.addEventListener("click", (event) => {
      if (suppressCardOpen) {
        event.preventDefault();
        return;
      }
      openLightbox(index);
    }));
    previous?.addEventListener("click", () => setIndex(activeIndex - 1));
    next?.addEventListener("click", () => setIndex(activeIndex + 1));
    lightboxPrevious?.addEventListener("click", () => setIndex(activeIndex - 1));
    lightboxNext?.addEventListener("click", () => setIndex(activeIndex + 1));
    lightboxZoom?.addEventListener("click", () => setZoomed(!lightbox.classList.contains("is-zoomed")));
    lightbox?.querySelectorAll("[data-comic-strip-close]").forEach((button) => button.addEventListener("click", closeLightbox));

    rail.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      pointerDrag = { pointerId: event.pointerId, startX: event.clientX, startScroll: rail.scrollLeft, moved: false };
      rail.setPointerCapture?.(event.pointerId);
      rail.classList.add("is-dragging");
    });

    rail.addEventListener("pointermove", (event) => {
      if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;
      const distance = event.clientX - pointerDrag.startX;
      if (Math.abs(distance) > 6) {
        pointerDrag.moved = true;
        suppressCardOpen = true;
      }
      rail.scrollLeft = pointerDrag.startScroll - distance;
    });

    function finishPointerDrag(event) {
      if (!pointerDrag || event.pointerId !== pointerDrag.pointerId) return;
      const moved = pointerDrag.moved;
      rail.releasePointerCapture?.(event.pointerId);
      rail.classList.remove("is-dragging");
      pointerDrag = null;
      if (!moved) {
        suppressCardOpen = false;
        return;
      }
      const nearest = cards.reduce((best, card, index) => (
        Math.abs(card.offsetLeft - rail.scrollLeft) < Math.abs(cards[best].offsetLeft - rail.scrollLeft) ? index : best
      ), 0);
      setTimeout(() => {
        setIndex(nearest);
        suppressCardOpen = false;
      }, 0);
    }

    rail.addEventListener("pointerup", finishPointerDrag);
    rail.addEventListener("pointercancel", finishPointerDrag);

    rail.addEventListener("scroll", () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const nearest = cards.reduce((best, card, index) => (
          Math.abs(card.offsetLeft - rail.scrollLeft) < Math.abs(cards[best].offsetLeft - rail.scrollLeft) ? index : best
        ), 0);
        if (nearest !== activeIndex) {
          activeIndex = nearest;
          updateCounter();
        }
      });
    }, { passive: true });

    rail.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setIndex(activeIndex - 1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setIndex(activeIndex + 1);
      }
    });

    window.addEventListener("resize", () => rail.scrollTo({ left: cards[activeIndex].offsetLeft }));
    document.addEventListener("keydown", (event) => {
      if (!isLightboxOpen()) return;
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") setIndex(activeIndex - 1);
      if (event.key === "ArrowRight") setIndex(activeIndex + 1);
    });

    updateCounter();
  }

  document.addEventListener("DOMContentLoaded", () => {
    initComicStripRail();
    initProductGallery();
    document.querySelectorAll(".drafted-product-form").forEach((form) => {
      syncVariant(form);
      form.addEventListener("change", () => syncVariant(form));
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        try { await addProduct(form); } catch (error) {
          const live = document.querySelector("[data-cart-live]");
          if (live) live.textContent = error.message;
          window.alert(error.message);
          syncVariant(form);
        }
      });
    });

    document.addEventListener("click", async (event) => {
      const open = event.target.closest("[data-cart-open]");
      const close = event.target.closest("[data-cart-close], [data-cart-backdrop]");
      const change = event.target.closest("[data-cart-change]");
      const filter = event.target.closest("[data-product-filter]");
      const filterLink = event.target.closest("[data-filter-link]");
      if (open) setCartOpen(true);
      if (close) setCartOpen(false);
      if (filter) setFilter(filter.dataset.productFilter);
      if (filterLink && document.querySelector("[data-product-grid]")) {
        event.preventDefault();
        setFilter(filterLink.dataset.filterLink);
        document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" });
      }
      if (change) {
        change.disabled = true;
        try { await changeLine(Number(change.dataset.line), Number(change.dataset.quantity) + Number(change.dataset.cartChange)); }
        catch (error) { window.alert(error.message); }
      }
    });

    document.addEventListener("keydown", (event) => { if (event.key === "Escape") setCartOpen(false); });
    getCart().catch(() => {});
  });
})();
